import type { PageSchema, PublishedVersion } from '../dataProvider';
import type { Block } from './blocks';

/**
 * The versioned page lifecycle (D-page-schema-editor-8b). PURE derivations off the
 * row, the ruleLifecycle / promoLifecycle idiom family, but keyed on CONTENT
 * EQUALITY (draft vs latest published) rather than the clock (the clock-derived
 * per-block scheduling is 8d). The CURRENT PUBLISHED is the LAST history entry,
 * derived, never a stored pointer (the ledger-current discipline).
 */

/** The current published version (the last history entry), or null if never published. */
export const currentPublished = (row: PageSchema): PublishedVersion | null => {
  const versions = row.versions ?? [];
  return versions.length ? versions[versions.length - 1] : null;
};

/** The next monotonic version int (increments on EVERY publish, including a rollback). */
export const nextVersion = (row: PageSchema): number => {
  const published = currentPublished(row);
  return (published?.version ?? 0) + 1;
};

/** Order-independent deep equality (object key order must not matter). */
export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const ak = Object.keys(ao);
    const bk = Object.keys(bo);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => k in bo && deepEqual(ao[k], bo[k]));
  }
  return false;
};

/** Do the draft blocks match the latest published blocks (no pending changes)? */
export const draftMatchesPublished = (row: PageSchema): boolean => {
  const published = currentPublished(row);
  if (!published) return false;
  return deepEqual(row.blocks as Block[], published.blocks);
};

/**
 * The three operationally meaningful states. `status` stays draft|published; this
 * folds in the DERIVED draft-ahead flag:
 *  - draft_only: never published (versions empty). Never served to apps.
 *  - published_clean: published AND the draft equals the latest published.
 *  - draft_ahead: published AND the draft differs from the latest published (the
 *    common editing state).
 */
export type PageLifecycle = 'draft_only' | 'published_clean' | 'draft_ahead';

export const pageLifecycle = (row: PageSchema): PageLifecycle => {
  if (!currentPublished(row)) return 'draft_only';
  return draftMatchesPublished(row) ? 'published_clean' : 'draft_ahead';
};

export const LIFECYCLE_LABEL: Record<PageLifecycle, string> = {
  draft_only: 'Draft',
  published_clean: 'Published',
  draft_ahead: 'Draft ahead',
};

export const LIFECYCLE_COLOR: Record<
  PageLifecycle,
  'default' | 'success' | 'warning'
> = {
  draft_only: 'default',
  published_clean: 'success',
  draft_ahead: 'warning',
};
